<script setup>
import { computed } from "vue";
import dayjs from "dayjs";
import "dayjs/locale/id";
dayjs.locale("id");

const props = defineProps({
  agenda: {
    type: Array,
    required: true,
  },
});

const items = computed(() => props.agenda?.data || []);

const formatTanggal = (tanggal) =>
  tanggal ? dayjs(tanggal).format("D MMMM YYYY") : null;
const day = (tanggal) => (tanggal ? dayjs(tanggal).format("DD") : "");
const monthYear = (tanggal) => (tanggal ? dayjs(tanggal).format("MMM YYYY") : "");
</script>

<template>
  <section class="pt-20 lg:pt-25 pb-15" v-if="items.length">
    <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
      <!-- Section header -->
      <div class="max-w-2xl mx-auto mb-10 text-center">
        <span
          class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10"
        >
          <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
          Agenda
        </span>
        <h2
          class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl font-roboto-slab"
        >
          Agenda Kegiatan
        </h2>
        <p class="mt-3 text-base leading-relaxed text-gray-500">
          Jadwal kegiatan dan acara yang akan datang.
        </p>
      </div>

      <!-- Agenda list -->
      <div class="space-y-6">
        <article
          v-for="(item, idx) in items"
          :key="idx"
          class="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-sm group rounded-2xl sm:flex-row hover:shadow-lg hover:-translate-y-0.5"
        >
          <!-- Image -->
          <div
            class="relative w-full overflow-hidden bg-gray-100 shrink-0 sm:w-72 lg:w-80 aspect-[16/10] sm:aspect-auto"
          >
            <img
              v-if="item.gambar"
              :src="item.gambar"
              :alt="item.judul || 'Agenda'"
              class="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
            />
            <div
              v-else
              class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5"
            >
              <i class="text-4xl fa-regular fa-calendar-days text-primary/40"></i>
            </div>

            <!-- Date badge -->
            <div
              v-if="item.tanggal_mulai"
              class="absolute flex flex-col items-center px-3 py-1.5 text-center bg-white shadow rounded-xl top-3 left-3"
            >
              <span class="text-lg font-bold leading-none text-primary">
                {{ day(item.tanggal_mulai) }}
              </span>
              <span
                class="mt-0.5 text-[10px] font-medium tracking-wide uppercase text-gray-500"
              >
                {{ monthYear(item.tanggal_mulai) }}
              </span>
            </div>
          </div>

          <!-- Content -->
          <div class="flex flex-col flex-1 p-5 sm:p-6">
            <h3
              class="text-lg font-bold leading-snug text-gray-900 transition-colors line-clamp-2 group-hover:text-primary font-roboto-slab"
            >
              {{ item.judul || "Judul Agenda" }}
            </h3>

            <p class="mt-2 text-sm leading-relaxed text-gray-500 line-clamp-2">
              {{ item.deskripsi || "Deskripsi agenda belum tersedia." }}
            </p>

            <div
              class="flex flex-wrap items-center mt-4 text-sm text-gray-500 gap-x-5 gap-y-2"
            >
              <span class="inline-flex items-center gap-2">
                <i class="fa-regular fa-calendar text-primary"></i>
                {{ formatTanggal(item.tanggal_mulai) || "Tanggal belum tersedia" }}
              </span>
              <span v-if="item.lokasi" class="inline-flex items-center gap-2">
                <i class="fa-solid fa-location-dot text-primary"></i>
                {{ item.lokasi }}
              </span>
            </div>

            <span
              v-if="item.status"
              class="inline-flex items-center self-start gap-1.5 mt-4 px-3 py-1 text-xs font-medium capitalize rounded-full text-primary bg-primary/10"
            >
              <i class="fa-solid fa-circle-check"></i>
              {{ item.status }}
            </span>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
